import { FileIcon } from '@/assets/icons'
import ConfirmationModal from '@components/ConfirmationModal'
import { useDeleteFile, useFiles, useUploadFiles } from '@hooks'
import { GetFileType } from '@orbit/api-types'
import React, { useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import FileViewer from '@components/FileViewer/FileViewer'
import { FileInput } from '@components/Field'
import {
	AddButton,
	EmptyAttachments,
	RemoveButton,
	SectionContainer,
	SectionHeader,
	SectionLabel,
	ThumbnailDocIcon,
	ThumbnailGrid,
	ThumbnailItem,
	UploadingOverlay,
} from './AttachmentSection.styles'
import Spinner from '@components/Spinner'
import { iconSizes } from '@/theme/tokens'

const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic']
const ACCEPTED_TYPES = [
	...IMAGE_MIME_TYPES,
	'application/pdf',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'application/vnd.ms-excel',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'application/vnd.ms-powerpoint',
	'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	'text/plain',
	'text/csv',
].join(',')

const MAX_ATTACHMENTS = 5

export type ResourceType = 'IDEA' | 'TRIP_EVENT' | 'ACCOMMODATION' | 'FLIGHT_GROUP'

interface AttachmentSectionProps {
	tripId: string
	resourceType: ResourceType
	resourceId: number | string | undefined
	fileIds: string[]
	onUploadComplete?: () => void
}

function isImage(file: GetFileType): boolean {
	return IMAGE_MIME_TYPES.includes(file.mimeType)
}

function getFileExtension(fileName: string): string {
	const parts = fileName.split('.')
	return parts.length > 1 ? parts[parts.length - 1] : ''
}

const AttachmentSection: React.FC<AttachmentSectionProps> = ({
	tripId,
	resourceType,
	resourceId,
	fileIds,
	onUploadComplete,
}) => {
	const { data: allFilesData } = useFiles(tripId)
	const uploadFiles = useUploadFiles()
	const deleteFile = useDeleteFile()
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [deleteTarget, setDeleteTarget] = useState<GetFileType | null>(null)
	const [lightboxFile, setLightboxFile] = useState<GetFileType | null>(null)
	// Track IDs added/removed this session so the UI updates without needing a prop refresh
	const [localAddedIds, setLocalAddedIds] = useState<string[]>([])
	const [localRemovedIds, setLocalRemovedIds] = useState<string[]>([])

	const effectiveFileIds = useMemo(() => {
		const merged = [...new Set([...fileIds, ...localAddedIds])]
		return merged.filter(id => !localRemovedIds.includes(id))
	}, [fileIds, localAddedIds, localRemovedIds])

	const attachments = useMemo(() => {
		if (!allFilesData?.files || effectiveFileIds.length === 0) return []
		return allFilesData.files.filter(f => effectiveFileIds.includes(f.id))
	}, [allFilesData?.files, effectiveFileIds])

	const remaining = MAX_ATTACHMENTS - effectiveFileIds.length
	const canAdd = remaining > 0 && !!resourceId

	const handleAddClick = () => {
		fileInputRef.current?.click()
	}

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const selected = e.target.files
		if (!selected || selected.length === 0 || !resourceId) return

		const fileArray = Array.from(selected)
		if (fileArray.length > remaining) {
			toast.error(`You can add ${remaining} more attachment${remaining !== 1 ? 's' : ''}.`)
			if (fileInputRef.current) fileInputRef.current.value = ''
			return
		}

		uploadFiles.mutate(
			{
				tripId,
				files: fileArray,
				resourceType,
				resourceId: typeof resourceId === 'string' ? parseInt(resourceId) : resourceId,
			},
			{
				onSuccess: (uploaded) => {
					setLocalAddedIds(prev => [...prev, ...uploaded.map(f => f.id)])
					toast.success('Attachment added')
					onUploadComplete?.()
				},
				onError: () => {
					toast.error('Failed to upload. Please try again.')
				},
				onSettled: () => {
					if (fileInputRef.current) fileInputRef.current.value = ''
				},
			},
		)
	}

	const handleRemoveClick = (e: React.MouseEvent, file: GetFileType) => {
		e.stopPropagation()
		setDeleteTarget(file)
	}

	const handleThumbnailClick = (file: GetFileType) => {
		if (isImage(file)) {
			setLightboxFile(file)
		} else {
			window.open(file.url, '_blank')
		}
	}

	const handleConfirmDelete = () => {
		if (!deleteTarget) return
		const idToRemove = deleteTarget.id
		deleteFile.mutate(
			{ tripId, fileId: idToRemove },
			{
				onSuccess: () => {
					setLocalRemovedIds(prev => [...prev, idToRemove])
					toast.success('Attachment removed')
					onUploadComplete?.()
				},
				onError: () => toast.error('Failed to remove attachment'),
			},
		)
	}

	return (
		<SectionContainer>
			<SectionHeader>
				<SectionLabel>
					<FileIcon />
					Attachments {effectiveFileIds.length > 0 && `(${effectiveFileIds.length}/${MAX_ATTACHMENTS})`}
				</SectionLabel>
				{effectiveFileIds.length > 0 && canAdd && (
					<AddButton type="button" onClick={handleAddClick} disabled={uploadFiles.isPending}>
						+ Add
					</AddButton>
				)}
			</SectionHeader>

			<FileInput
				ref={fileInputRef}
				multiple
				accept={ACCEPTED_TYPES}
				onChange={handleFileChange}
			/>

			{uploadFiles.isPending && (
				<UploadingOverlay>
					<Spinner size={14} />
					Uploading...
				</UploadingOverlay>
			)}

			{effectiveFileIds.length === 0 && !uploadFiles.isPending ? (
				<EmptyAttachments type="button" onClick={handleAddClick} disabled={!canAdd}>
					<FileIcon sx={{ fontSize: iconSizes.xs }} />
					{canAdd ? 'Add attachments' : 'Save first to add attachments'}
				</EmptyAttachments>
			) : (
				<ThumbnailGrid>
					{attachments.map(file => (
						<ThumbnailItem key={file.id} onClick={() => handleThumbnailClick(file)} title={file.fileName}>
							{isImage(file) ? (
								<img src={file.url} alt={file.fileName} loading="lazy" />
							) : (
								<ThumbnailDocIcon>
									<FileIcon sx={{ fontSize: iconSizes.xl }} />
									<span>{getFileExtension(file.fileName)}</span>
								</ThumbnailDocIcon>
							)}
							<RemoveButton onClick={e => handleRemoveClick(e, file)} title="Remove">
								✕
							</RemoveButton>
						</ThumbnailItem>
					))}
				</ThumbnailGrid>
			)}

			<ConfirmationModal
				isOpen={!!deleteTarget}
				onClose={() => setDeleteTarget(null)}
				onConfirm={handleConfirmDelete}
				title="Remove Attachment"
				message={
					<>
						Remove <strong>{deleteTarget?.fileName}</strong>?
					</>
				}
				confirmText="Remove"
				variant="danger"
			/>

			<FileViewer file={lightboxFile} onClose={() => setLightboxFile(null)} onDownloadError={() => toast.error('Failed to download file')} />
		</SectionContainer>
	)
}

export default AttachmentSection
